import type { EcartDuDebloquant, JourneeDuJour, TempsDeJAi, LigneDuPourquoi, LigneVivier, Vivier } from '../partage/api/types';
import { enFrancais, heureLisible, joursEntre } from '../partage/datesEnMots';
import {
  AUCUNE_REPONSE_ATTENDUE,
  DEMANDE_PLUS_DE_COURAGE,
  IL_FAUT_SORTIR,
  MA_JOURNEE,
  majuscule,
  nChoses,
  PLUS_LONG_QUE,
  proprietesEnMots,
} from '../partage/vocabulaire';

/**
 * Les mots de « Maintenant » sur l'ordinateur, sans rendu (chantier `revue-design`, ticket 01,
 * `design/maintenant.html`). Rien ne s'y calcule sur les priorités ni sur les marges : tout ce
 * qui se dit vient du serveur, on le choisit et on le met en phrase.
 */

type UnDebloquant = Vivier['debloquants'][number];

/**
 * Le débloquant montré : « Une autre » avance d'un rang, et revient au premier après le
 * dernier. Le rang se garde quand la liste change (une tâche cochée) : il reste dans la liste.
 */
export function debloquantAuRang(debloquants: UnDebloquant[], rang: number): UnDebloquant | null {
  if (debloquants.length === 0) return null;
  const i = ((rang % debloquants.length) + debloquants.length) % debloquants.length;
  return debloquants[i] ?? null;
}

/**
 * La mention d'une ligne de Prioritaire, avant ses étoiles : sa raison, puis « Prévue jeudi »
 * quand elle est prévue un autre jour (décision d'Igor du 2026-10-03, spec `maintenant`), puis
 * sa durée. « Marge de 2 jours · Prévue jeudi · 30 min ». Le serveur écrit la raison et la
 * mention ; ici, on ne fait que les assembler, chacune avec sa majuscule.
 */
export function raisonDePrioritaire(ligne: LigneDuPourquoi): string {
  return [
    ligne.pourquoi ? majuscule(ligne.pourquoi) : null,
    ligne.prevue ? majuscule(ligne.prevue) : null,
    ...proprietesEnMots(ligne, ['duree']),
  ]
    .filter((m): m is string => Boolean(m))
    .join(' · ');
}

/**
 * La marge du débloquant, sous ses pastilles. Le serveur ne la rend pas sur `debloquants` : on
 * reprend le pourquoi de sa ligne dans Prioritaire, « Pendant ce temps » ou sous l'eau quand il
 * y dit une marge (« Marge de 5 jours », « L'échéance du 29 septembre est passée »). « Libère N
 * choses » se tait, la carte le dit déjà. Sans pourquoi utile, son échéance s'il en a une
 * (« Avant le 12 octobre ») ; sinon rien.
 */
export function margeDuDebloquant(bulle: LigneVivier, vivier: Vivier): string | null {
  const lignes = [
    ...vivier.prioritaire,
    ...[...vivier.pendantCeTemps, ...vivier.sousLEau].flatMap((l) => [l, ...l.pliees]),
  ];
  const sienne = lignes.find((l) => l.id === bulle.id);
  if (sienne && sienne.pourquoi && !/^lib[èe]re\b/i.test(sienne.pourquoi)) return majuscule(sienne.pourquoi);
  if (bulle.dateEcheance) return `Avant le ${enFrancais(bulle.dateEcheance)}`;
  return null;
}

/**
 * La ligne du débloquant qui ne colle pas aux réglages, sous ses pastilles (chantier
 * `flottaison-suite`, ticket 10) : « Plus long que tes 30 min · Il faut sortir de chez toi ».
 * Le serveur dit les écarts ; ici, on les met en mots, dans son ordre. Rien quand il colle.
 */
export function ligneDesEcarts(ecarts: EcartDuDebloquant[], temps: TempsDeJAi | null): string | null {
  const mots = ecarts
    .map((e) => {
      if (e === 'temps') return temps ? PLUS_LONG_QUE[temps] : null;
      if (e === 'lieu') return IL_FAUT_SORTIR;
      return DEMANDE_PLUS_DE_COURAGE;
    })
    .filter((m): m is string => Boolean(m));
  return mots.length > 0 ? mots.join(' · ') : null;
}

/**
 * La bande « À vérifier », en une phrase : « 2 réponses attendues. La prochaine demain :
 * « Décrire mes clients ». » Ce qui dure trop se compte (« 1 à relancer »), jamais en rouge, jamais
 * un reproche. La prochaine est l'attente en cours qui doit revenir le plus tôt.
 */
export function phraseAVerifier(lignes: LigneVivier[], aujourdhui: string): string {
  if (lignes.length === 0) return AUCUNE_REPONSE_ATTENDUE;
  const n = lignes.length;
  const morceaux = [`${n} ${n > 1 ? 'réponses attendues' : 'réponse attendue'}.`];
  const relances = lignes.filter((l) => l.situation.nom === 'a_verifier').length;
  if (relances > 0) morceaux.push(`${relances} à relancer.`);
  const enCours = lignes
    .filter((l) => l.situation.nom === 'en_attente' && l.situation.attenduLe)
    .sort((a, b) => (a.situation.attenduLe ?? '').localeCompare(b.situation.attenduLe ?? ''))[0];
  if (enCours?.situation.attenduLe) {
    morceaux.push(`La prochaine ${quand(enCours.situation.attenduLe, aujourdhui)} : « ${enCours.titre} ».`);
  }
  return morceaux.join(' ');
}

/** « aujourd'hui », « demain », « le 12 octobre ». */
function quand(jour: string, aujourdhui: string): string {
  const ecart = joursEntre(aujourdhui, jour);
  if (ecart === 0) return 'aujourd’hui';
  if (ecart === 1) return 'demain';
  return `le ${enFrancais(jour)}`;
}

/**
 * Le lien « Ma journée » en haut de « Maintenant » sur l'ordinateur (chantier `revue-design`,
 * ticket 01, `design/maintenant.html`) : « Ma journée : 4 choses, prochaine à 9 h 30 ». Sans
 * journée, « Ma journée : rien de prévu » ; tout coché, « Ma journée : 4 choses, toutes faites ».
 * La prochaine est la première pas encore faite, par l'heure. Venu d'`aujourdhuiEnMots.ts`
 * (chantier nettoyage-mots, ticket 01).
 */
export function lienDeMaJournee(journee: JourneeDuJour['journee']): string {
  if (!journee || journee.taches.length === 0) return `${MA_JOURNEE} : rien de prévu`;
  const restent = journee.taches.filter((t) => t.etat !== 'fait');
  const suivante = [...restent].sort((a, b) => a.heure.localeCompare(b.heure))[0];
  const suite = suivante ? `prochaine à ${heureLisible(suivante.heure.slice(0, 5))}` : 'toutes faites';
  return `${MA_JOURNEE} : ${nChoses(journee.taches.length)}, ${suite}`;
}
